/**
 * Per-line gift wrapping.
 *
 * Model:
 *  - Each "wrap" is a real Shopify line item (variant from a link in the
 *    "gift-wrapping" Navigation menu).
 *  - It is bound to its parent line via line item property _for_key.
 *    Without that property Shopify would merge wraps for different parents
 *    into a single line, since they share the variant id.
 *  - The wrap's quantity mirrors the parent's quantity (per_unit pricing).
 *    This is achieved by rendering a hidden .cart-item__quantity-input
 *    inside the parent's cart-item; cart-form sweeps it into the same
 *    /cart/update.js POST as the parent quantity, so the two stay in lock
 *    step in a single round-trip.
 *  - Personal message is stored on the wrap line as _gift_note (per line).
 *
 * When theme.settings.giftWrapCartTransform is on, the wrap is not a
 * product. The garment stores _gift_wrap, _gift_wrap_variant,
 * _gift_wrap_title, and _gift_note, and Cart Transform adds the fee.
 * Leftover wrap product lines are removed so they are not charged twice.
 *
 * Cart attribute _gift_klav_total stores the gift-packaging amount in
 * major currency units (e.g. "3.00") so Klaviyo abandoned-cart emails
 * can subtract it from $value after hiding gift-klav line items.
 */

(function () {
  const GIFT_WRAP_KEY = '_gift_wrap';
  const GIFT_WRAP_VARIANT_KEY = '_gift_wrap_variant';
  const GIFT_WRAP_TITLE_KEY = '_gift_wrap_title';
  const GIFT_NOTE_KEY = '_gift_note';
  const FOR_KEY_KEY = '_for_key';
  const GIFT_FOR_KEY = '_gift_for';
  const GIFT_KLAV_TOTAL_ATTR = '_gift_klav_total';
  const GIFT_WRAP_FEE_UNIT_CENTS = 300;
  const MIRROR_SELECTOR = '.giftwrap-line__qty-mirror';

  function usesCartTransform() {
    return !!(window.theme && window.theme.settings && window.theme.settings.giftWrapCartTransform);
  }

  function fetchCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((response) => {
      if (!response.ok) throw new Error(response.statusText || 'cart fetch failed');
      return response.json();
    });
  }

  function wrapFlagOn(item) {
    const source = (item && item.properties) || {};
    const value = source[GIFT_WRAP_KEY];
    if (value == null) return false;
    return ['yes', 'true', '1'].indexOf(String(value).trim().toLowerCase()) !== -1;
  }

  function findChangedLine(cart, previousLine, wantWrap) {
    const items = (cart && cart.items) || [];
    const sameKey = items.find((it) => String(it.key) === String(previousLine.key));
    if (sameKey) return sameKey;
    const variantId = String(previousLine.variant_id);
    const matches = items.filter((it) => String(it.variant_id) === variantId && wrapFlagOn(it) === wantWrap);
    if (matches.length === 1) return matches[0];
    return matches.find((it) => it.quantity === previousLine.quantity) || null;
  }

  function lineProperties(item) {
    const source = (item && item.properties) || {};
    const properties = {};
    Object.keys(source).forEach((key) => {
      if (source[key] != null && source[key] !== '') properties[key] = String(source[key]);
    });
    return properties;
  }

  /**
   * cart/change.js replaces the whole properties hash, but an empty object
   * is ignored and a missing key is kept. Clearing a property means sending
   * that key with an empty value. Form encoding keeps those empty values;
   * JSON drops them.
   */
  function changeLineProperties(lineKey, quantity, properties) {
    const body = new URLSearchParams();
    body.set('id', String(lineKey));
    body.set('quantity', String(quantity));
    Object.keys(properties).forEach((key) => {
      const value = properties[key] == null ? '' : String(properties[key]);
      body.append(`properties[${key}]`, value);
    });
    return fetch(cartRoute('cartChange', '/cart/change.js'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
        Accept: 'application/json',
      },
      body: body.toString(),
    }).then((response) => {
      if (!response.ok) throw new Error(response.statusText || 'cart change failed');
      return response.json();
    });
  }

  let propertyQueue = Promise.resolve();
  function enqueuePropertyUpdate(task) {
    const run = propertyQueue.then(task, task);
    propertyQueue = run.then(() => {}, () => {});
    return run;
  }

  function cartFormCount() {
    return document.querySelectorAll('cart-form[data-ajax-update="true"]').length || 1;
  }

  /**
   * Section HTML is fetched when on:cart:change fires. The cart transform
   * price can land one calculation later, so wait for that paint, then
   * fetch the sections once more.
   */
  function refreshCartUi() {
    const expected = cartFormCount();
    return new Promise((resolve) => {
      let seen = 0;
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        document.removeEventListener('on:cart:after-merge', onMerged);
        clearTimeout(timer);
        resolve();
      };
      const onMerged = () => {
        seen += 1;
        if (seen >= expected) finish();
      };
      const timer = setTimeout(finish, 3000);
      document.addEventListener('on:cart:after-merge', onMerged);
      dispatchCartChange();
    });
  }

  function cartRoute(name, fallback) {
    if (window.theme && window.theme.routes && window.theme.routes[name]) {
      return window.theme.routes[name];
    }
    return fallback;
  }

  function dispatchCartChange() {
    document.dispatchEvent(
      new CustomEvent('on:cart:change', { bubbles: true, cancelable: false })
    );
  }

  /**
   * Swym "Save for later" fires on any cart line removal, including gift
   * packaging (a real Shopify line item). Tear down that modal when the
   * customer removes wrap from the per-line panel or when orphan wraps
   * are reconciled — without disabling SFL for normal cart products.
   */
  let swymSflSuppressUntil = 0;
  let swymSflSuppressObserver = null;

  function swymUnlockScroll() {
    document.body.classList.remove('swym-no-scroll', 'swym-modal-open');
    document.documentElement.classList.remove('swym-no-scroll', 'swym-modal-open');
    if (document.body.style.overflow === 'hidden') document.body.style.overflow = '';
  }

  function tearDownSwymSflModal() {
    const modal = document.querySelector('.swym-modal.swym-sfl-popup');
    if (!modal || !modal.isConnected) return;

    const box = modal.closest('.swym-modal-container');
    if (box && box.parentNode) {
      box.parentNode.removeChild(box);
    } else if (modal.parentNode) {
      modal.parentNode.removeChild(modal);
    }

    swymUnlockScroll();
  }

  function armSwymSflSuppression(durationMs) {
    swymSflSuppressUntil = Date.now() + (durationMs || 5000);
    tearDownSwymSflModal();

    if (swymSflSuppressObserver) return;

    swymSflSuppressObserver = new MutationObserver(() => {
      if (Date.now() > swymSflSuppressUntil) return;
      if (document.querySelector('.swym-modal.swym-sfl-popup')) {
        tearDownSwymSflModal();
      }
    });
    swymSflSuppressObserver.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  }

  function debounce(fn, wait) {
    let t;
    return function debounced(...args) {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  /**
   * Scroll-lock watchdog + diagnostic.
   *
   * Two things bundled here:
   *
   * 1. cart-form's refresh() (assets/main.js) doesn't .catch() /
   *    .finally(), so a failed fetch leaves `cart-form--refreshing`
   *    on the cart-form forever (pointer-events: none on touch
   *    devices). We sweep it a few seconds after each cart change.
   *
   * 2. Diagnostic: snapshot html / body / open drawers + modals
   *    after each cart action. The user reports the page scrollbar
   *    disappearing after add+remove of a wrap. The page only loses
   *    its scrollbar via `html:has(.drawer[open])` or
   *    `html:has(.modal[open])`, so dumping which element still has
   *    [open] (and what html/body overflow are computed to) tells
   *    us which path is stuck. Logged via console.warn so it's
   *    impossible to miss in DevTools. Remove once root-caused.
   */
  let scrollWatchdogId = 0;
  function logScrollState(label) {
    try {
      const html = document.documentElement;
      const body = document.body;
      const htmlOv = getComputedStyle(html).overflow;
      const bodyOvY = getComputedStyle(body).overflowY;
      const opens = Array.from(document.querySelectorAll('[open]'))
        .filter((el) => el.tagName !== 'DETAILS')
        .map((el) => `${el.tagName.toLowerCase()}.${(el.className || '').toString().trim().split(/\s+/).slice(0, 3).join('.')}`);
      // eslint-disable-next-line no-console
      console.warn(
        `[gift-wrapping diag] ${label}`,
        '\n  html.overflow=', htmlOv,
        '\n  body.overflow-y=', bodyOvY,
        '\n  body.style.overflow=', body.style.overflow || '(empty)',
        '\n  scrollHeight=', body.scrollHeight, '/ innerHeight=', window.innerHeight,
        '\n  [open] elements:', opens.length ? opens : '(none)',
      );
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[gift-wrapping diag] failed to log', e);
    }
  }

  function scheduleScrollWatchdog() {
    if (scrollWatchdogId) clearTimeout(scrollWatchdogId);
    scrollWatchdogId = setTimeout(() => {
      scrollWatchdogId = 0;
      document.querySelectorAll('.cart-form--refreshing').forEach((el) => {
        el.classList.remove('cart-form--refreshing');
      });
      document.querySelectorAll('.merge-item-refreshing').forEach((el) => {
        el.classList.remove('merge-item-refreshing');
      });
      logScrollState('watchdog tick (4s after cart change)');
    }, 4000);
  }
  document.addEventListener('on:cart:change', () => {
    logScrollState('on:cart:change');
    scheduleScrollWatchdog();
  });
  document.addEventListener('on:cart:after-merge', () => {
    logScrollState('on:cart:after-merge');
    if (scrollWatchdogId) {
      clearTimeout(scrollWatchdogId);
      scrollWatchdogId = 0;
    }
  });

  /**
   * Per-line panel.
   * Lives inside one cart-item; reads parent meta from data-* attributes
   * the snippet emits, and either:
   *  - line-item mode: ADD posts cart/add.js; remove sets wrap qty to 0
   *  - cart-transform mode: ADD writes _gift_wrap on the garment; remove
   *    clears those properties
   */
  class AesthetGiftWrapLine extends HTMLElement {
    connectedCallback() {
      if (this.__wired) return;
      this.__wired = true;

      this.parentKey = this.dataset.parentKey;
      this.parentQty = parseInt(this.dataset.parentQuantity, 10) || 1;
      this.parentTitle = this.dataset.parentTitle || '';

      this.toggle = this.querySelector('.js-giftwrap-toggle');
      this.expanded = this.querySelector('.js-giftwrap-expanded');
      this.select = this.querySelector('.js-giftwrap-select');
      this.note = this.querySelector('.js-giftwrap-note');
      this.addBtn = this.querySelector('.js-giftwrap-add');
      this.addHoverWrap = this.querySelector('.giftwrap-line__add-hover-wrap');
      this.addTooltip = this.querySelector('.giftwrap-line__add-tooltip');
      this.removeBtn = this.querySelector('.js-giftwrap-remove');
      this.noteDisplay = this.querySelector('.js-giftwrap-note-display');
      this.noteEditBtn = this.querySelector('.js-giftwrap-note-edit');
      this.noteEditPanel = this.querySelector('.js-giftwrap-note-edit-panel');
      this.noteEditInput = this.querySelector('.js-giftwrap-note-edit-input');
      this.noteSaveBtn = this.querySelector('.js-giftwrap-note-save');
      this.noteCancelBtn = this.querySelector('.js-giftwrap-note-cancel');

      if (this.toggle) {
        this.toggle.addEventListener('click', this.onToggle.bind(this));
      }
      if (this.addBtn) {
        this.addBtn.addEventListener('click', this.onAdd.bind(this));
      }
      if (this.addHoverWrap) {
        this.bindAddTooltip();
      }
      if (this.removeBtn) {
        this.removeBtn.addEventListener('click', this.onRemove.bind(this));
      }
      if (this.noteEditBtn) {
        this.noteEditBtn.addEventListener('click', this.onEditNote.bind(this));
      }
      if (this.noteSaveBtn) {
        this.noteSaveBtn.addEventListener('click', this.onSaveNote.bind(this));
      }
      if (this.noteCancelBtn) {
        this.noteCancelBtn.addEventListener('click', this.onCancelNoteEdit.bind(this));
      }
      this.addEventListener('click', this.onPackagingOption.bind(this));
      this.syncAddButtonState();
    }

    bindAddTooltip() {
      this.addHoverWrap.addEventListener('mouseenter', () => {
        if (this.isAddInactive()) this.showAddTooltip();
      });
      this.addHoverWrap.addEventListener('mouseleave', () => {
        this.hideAddTooltip();
      });
    }

    isAddInactive() {
      return !!(this.addBtn && this.addBtn.getAttribute('aria-disabled') === 'true');
    }

    hideAddTooltip() {
      if (!this.addHoverWrap || !this.addTooltip) return;
      this.addHoverWrap.classList.remove('giftwrap-line__add-hover-wrap--tooltip-open');
      this.addTooltip.setAttribute('aria-hidden', 'true');
      if (this.addBtn) this.addBtn.removeAttribute('aria-describedby');
      if (this._addTooltipHideTimer) {
        clearTimeout(this._addTooltipHideTimer);
        this._addTooltipHideTimer = null;
      }
      if (this._addTooltipDocClick) {
        document.removeEventListener('click', this._addTooltipDocClick, true);
        this._addTooltipDocClick = null;
      }
    }

    showAddTooltip() {
      if (!this.addHoverWrap || !this.addTooltip || !this.addBtn || !this.isAddInactive()) return;

      this.addHoverWrap.classList.add('giftwrap-line__add-hover-wrap--tooltip-open');
      this.addTooltip.setAttribute('aria-hidden', 'false');
      if (this.addTooltip.id) {
        this.addBtn.setAttribute('aria-describedby', this.addTooltip.id);
      }

      if (!this._addTooltipDocClick) {
        this._addTooltipDocClick = (evt) => {
          if (this.addHoverWrap && !this.addHoverWrap.contains(evt.target)) {
            this.hideAddTooltip();
          }
        };
        document.addEventListener('click', this._addTooltipDocClick, true);
      }
    }

    scheduleAddTooltipHide() {
      if (this._addTooltipHideTimer) clearTimeout(this._addTooltipHideTimer);
      this._addTooltipHideTimer = setTimeout(() => {
        this._addTooltipHideTimer = null;
        this.hideAddTooltip();
      }, 3000);
    }

    onPackagingOption(event) {
      const option = event.target.closest('.js-giftwrap-option');
      if (!option || !this.contains(option)) return;
      event.preventDefault();
      event.stopPropagation();
      if (!this.select) return;

      const variantId = option.dataset.variantId;
      if (!variantId) return;

      this.querySelectorAll('.js-giftwrap-option').forEach((el) => {
        const selected = el === option;
        el.classList.toggle('giftwrap-line__option--selected', selected);
        el.setAttribute('aria-checked', selected ? 'true' : 'false');
      });
      this.select.value = variantId;
      this.syncAddButtonState();
    }

    syncAddButtonState() {
      if (!this.addBtn) return;
      const hasSelection = !!(this.select && this.select.value);
      if (hasSelection) {
        this.addBtn.removeAttribute('aria-disabled');
        this.addBtn.classList.remove('giftwrap-line__add--inactive');
        this.hideAddTooltip();
      } else {
        this.addBtn.setAttribute('aria-disabled', 'true');
        this.addBtn.classList.add('giftwrap-line__add--inactive');
      }
    }

    onToggle(event) {
      event.preventDefault();
      event.stopPropagation();
      if (!this.expanded || !this.toggle) return;
      const open = this.toggle.getAttribute('aria-expanded') !== 'true';
      this.toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      this.expanded.hidden = !open;
    }

    onAdd(event) {
      event.preventDefault();
      event.stopPropagation();
      if (this.__busy) return;

      const variantId = this.select && this.select.value;
      if (!variantId) {
        this.showAddTooltip();
        this.scheduleAddTooltipHide();
        return;
      }
      this.hideAddTooltip();
      const note = (this.note && this.note.value || '').trim();

      if (usesCartTransform()) {
        const selectedOption = this.querySelector('.js-giftwrap-option[aria-checked="true"]');
        const title = (selectedOption && selectedOption.dataset.label) || '';
        this.setBusy(true);
        this.updateParentProperties({ wrap: true, variantId, note, title })
          .catch((err) => {
            // eslint-disable-next-line no-console
            console.error('[gift-wrapping] add failed', err);
          })
          .finally(() => {
            this.setBusy(false);
            scheduleScrollWatchdog();
          });
        return;
      }

      const quantity = Math.max(1, this.parentQty);

      const properties = {};
      properties[FOR_KEY_KEY] = this.parentKey;
      if (this.parentTitle) properties[GIFT_FOR_KEY] = this.parentTitle;
      if (note) properties[GIFT_NOTE_KEY] = note;

      const body = {
        items: [
          {
            id: parseInt(variantId, 10),
            quantity,
            properties,
          },
        ],
      };

      this.setBusy(true);
      fetch(cartRoute('cartAdd', '/cart/add.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      })
        .then((response) => {
          if (!response.ok) throw new Error(response.statusText || 'add failed');
          return response.json();
        })
        .then(() => {
          dispatchCartChange();
        })
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.error('[gift-wrapping] add failed', err);
        })
        .finally(() => {
          this.setBusy(false);
          scheduleScrollWatchdog();
        });
    }

    onEditNote(event) {
      event.preventDefault();
      event.stopPropagation();
      if (!this.noteEditPanel || !this.noteDisplay) return;
      this.noteDisplay.hidden = true;
      this.noteEditPanel.hidden = false;
      if (this.noteEditInput) {
        this.noteEditInput.focus();
        const len = this.noteEditInput.value.length;
        this.noteEditInput.setSelectionRange(len, len);
      }
    }

    onCancelNoteEdit(event) {
      event.preventDefault();
      event.stopPropagation();
      this.closeNoteEdit();
    }

    closeNoteEdit() {
      if (this.noteEditPanel) this.noteEditPanel.hidden = true;
      if (this.noteDisplay) this.noteDisplay.hidden = false;
      if (this.noteEditInput) {
        const fullNote = this.noteEditInput.dataset.fullNote || '';
        this.noteEditInput.value = fullNote;
      }
    }

    updateParentProperties(change) {
      return enqueuePropertyUpdate(() => fetchCart().then((cart) => {
        const line = (cart.items || []).find((it) => String(it.key) === String(this.parentKey));
        if (!line) throw new Error('parent line missing');

        const properties = lineProperties(line);
        if (change.wrap) {
          properties[GIFT_WRAP_KEY] = 'yes';
          properties[GIFT_WRAP_VARIANT_KEY] = String(change.variantId || properties[GIFT_WRAP_VARIANT_KEY] || '');
          properties[GIFT_WRAP_TITLE_KEY] = String(change.title || properties[GIFT_WRAP_TITLE_KEY] || '');
          properties[GIFT_NOTE_KEY] = (change.note || '').trim();
        } else {
          properties[GIFT_WRAP_KEY] = '';
          properties[GIFT_WRAP_VARIANT_KEY] = '';
          properties[GIFT_WRAP_TITLE_KEY] = '';
          properties[GIFT_NOTE_KEY] = '';
        }
        return changeLineProperties(line.key, line.quantity, properties).then((nextCart) => {
          const updated = findChangedLine(nextCart, line, !!change.wrap);
          if (updated && this.isConnected) this.parentKey = updated.key;
          return refreshCartUi().then(() => refreshCartUi());
        });
      }));
    }

    buildWrapProperties(note) {
      const properties = {};
      properties[FOR_KEY_KEY] = this.parentKey;
      if (this.parentTitle) properties[GIFT_FOR_KEY] = this.parentTitle;
      const trimmed = (note || '').trim();
      properties[GIFT_NOTE_KEY] = trimmed;
      return properties;
    }

    onSaveNote(event) {
      event.preventDefault();
      event.stopPropagation();
      if (this.__busy) return;

      if (!this.noteEditInput) return;

      const note = this.noteEditInput.value;
      if (usesCartTransform()) {
        this.setBusy(true);
        this.updateParentProperties({
          wrap: true,
          variantId: this.dataset.wrapVariantId,
          title: this.dataset.wrapTitle,
          note,
        })
          .catch((err) => {
            // eslint-disable-next-line no-console
            console.error('[gift-wrapping] note update failed', err);
          })
          .finally(() => {
            this.setBusy(false);
            scheduleScrollWatchdog();
          });
        return;
      }

      const wrapKey = this.dataset.wrapLineKey;
      if (!wrapKey) return;
      const properties = this.buildWrapProperties(note);

      this.setBusy(true);
      fetch(cartRoute('cartChange', '/cart/change.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: wrapKey, properties }),
      })
        .then((response) => {
          if (!response.ok) throw new Error(response.statusText || 'note update failed');
          return response.json();
        })
        .then(() => {
          dispatchCartChange();
        })
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.error('[gift-wrapping] note update failed', err);
        })
        .finally(() => {
          this.setBusy(false);
          scheduleScrollWatchdog();
        });
    }

    onRemove(event) {
      event.preventDefault();
      event.stopPropagation();
      if (this.__busy) return;

      if (usesCartTransform()) {
        this.setBusy(true);
        this.updateParentProperties({ wrap: false })
          .catch((err) => {
            // eslint-disable-next-line no-console
            console.error('[gift-wrapping] remove failed', err);
          })
          .finally(() => {
            this.setBusy(false);
            scheduleScrollWatchdog();
          });
        return;
      }

      const wrapKey = this.dataset.wrapLineKey;
      if (!wrapKey) return;

      armSwymSflSuppression();
      this.setBusy(true);
      fetch(cartRoute('cartChange', '/cart/change.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: wrapKey, quantity: 0 }),
      })
        .then((response) => {
          if (!response.ok) throw new Error(response.statusText || 'remove failed');
          return response.json();
        })
        .then(() => {
          tearDownSwymSflModal();
          dispatchCartChange();
        })
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.error('[gift-wrapping] remove failed', err);
        })
        .finally(() => {
          tearDownSwymSflModal();
          this.setBusy(false);
          scheduleScrollWatchdog();
        });
    }

    setBusy(busy) {
      this.__busy = !!busy;
      this.classList.toggle('giftwrap-line--busy', this.__busy);
    }
  }

  customElements.define('aesthet-gift-wrap-line', AesthetGiftWrapLine);

  /**
   * Mirror sync.
   *
   * cart-form's adjustItemQuantity sweeps every non-disabled
   * .cart-item__quantity-input into one POST. Each rendered wrap line
   * emits a hidden mirror input next to its parent's qty input; we just
   * have to set its `value` to match what cart-form is about to write
   * to the visible parent input.
   *
   * Capture phase guarantees we run before cart-form's bubble listeners,
   * so we read the still-current parent value, compute the delta the
   * way cart-form does, then update the mirror.
   */
  function findMirror(cartItem) {
    if (!cartItem) return null;
    return cartItem.querySelector(MIRROR_SELECTOR);
  }

  function findParentQtyInput(cartItem) {
    if (!cartItem) return null;
    return cartItem.querySelector('.cart-item__quantity-input:not(.giftwrap-line__qty-mirror)');
  }

  function setMirror(cartItem, value) {
    const mirror = findMirror(cartItem);
    if (!mirror) return;
    const next = Math.max(0, parseInt(value, 10) || 0);
    if (mirror.value !== String(next)) {
      mirror.value = String(next);
    }
  }

  document.addEventListener(
    'click',
    (e) => {
      const cartItem = e.target.closest && e.target.closest('.cart-item');
      if (!cartItem) return;
      if (!findMirror(cartItem)) return;

      const parentInput = findParentQtyInput(cartItem);
      if (!parentInput || parentInput.disabled) return;

      const removeBtn = e.target.closest('.cart-item__remove, .cart-item__move-to-wishlist');
      if (removeBtn) {
        /* "Remove from wishlist" keeps the cart line — do not zero the wrap mirror. */
        if (
          removeBtn.matches('.cart-item__move-to-wishlist') &&
          removeBtn.classList.contains('is-cart-wishlisted')
        ) {
          return;
        }
        setMirror(cartItem, 0);
        return;
      }

      const up = e.target.closest('.quantity-up');
      const down = e.target.closest('.quantity-down');
      if (up && !up.classList.contains('unusable')) {
        const current = parseInt(parentInput.value, 10) || 0;
        setMirror(cartItem, current + 1);
        return;
      }
      if (down && !down.classList.contains('unusable')) {
        const current = parseInt(parentInput.value, 10) || 0;
        setMirror(cartItem, current - 1);
      }
    },
    true
  );

  document.addEventListener(
    'change',
    (e) => {
      const target = e.target;
      if (!target || !target.classList) return;
      if (
        !target.classList.contains('cart-item__quantity-input') ||
        target.classList.contains('giftwrap-line__qty-mirror')
      ) {
        return;
      }
      const cartItem = target.closest('.cart-item');
      if (!cartItem) return;
      setMirror(cartItem, target.value);
    },
    true
  );

  /**
   * Orphan reconciler.
   *
   * Most parent-side updates (qty +/-, remove) are caught by the mirror
   * mechanism above. The one case it cannot cover is when a parent line
   * is removed-and-re-added by another pipeline (e.g. variant swap in
   * cart-drawer pairing) — the old parent.key disappears and the wrap
   * line's _for_key now points at nothing.
   *
   * We listen for on:cart:change, fetch /cart.js, and remove any wrap
   * line whose _for_key references a key not present in the cart.
   * A re-entrancy flag prevents the dispatch we issue from triggering
   * another reconciliation pass.
   */
  let reconciling = false;
  let syncingGiftKlavTotal = false;

  function getWrapVariantIdSet() {
    const set = new Set();
    const fromTheme = window.theme && window.theme.settings && window.theme.settings.giftWrapVariantIds;
    if (Array.isArray(fromTheme)) {
      fromTheme.forEach((id) => {
        if (id) set.add(String(id));
      });
    }
    document.querySelectorAll('aesthet-gift-wrap-line').forEach((el) => {
      const v = el.dataset.wrapVariantId;
      if (v) set.add(String(v));
    });
    document
      .querySelectorAll('.js-giftwrap-select, .js-giftwrap-option[data-variant-id]')
      .forEach((opt) => {
        const v = opt.value || opt.dataset.variantId;
        if (v) set.add(String(v));
      });
    return set;
  }

  function formatGiftKlavTotal(cents) {
    const safe = Math.max(0, parseInt(cents, 10) || 0);
    return (safe / 100).toFixed(2);
  }

  /**
   * Amount Klaviyo should subtract from $value after hiding gift-klav rows.
   * Line-item mode: sum wrap product line prices.
   * Cart-transform mode: €3 (300 cents) × qty per wrapped garment line.
   */
  function computeGiftKlavTotalCents(cart) {
    const items = (cart && cart.items) || [];
    if (usesCartTransform()) {
      let cents = 0;
      items.forEach((it) => {
        if (!wrapFlagOn(it)) return;
        cents += GIFT_WRAP_FEE_UNIT_CENTS * (parseInt(it.quantity, 10) || 0);
      });
      return cents;
    }

    const wrapIds = getWrapVariantIdSet();
    let cents = 0;
    items.forEach((it) => {
      if (!wrapIds.has(String(it.variant_id || it.id))) return;
      const lineCents = it.final_line_price != null ? it.final_line_price : it.line_price;
      cents += parseInt(lineCents, 10) || 0;
    });
    return cents;
  }

  function syncGiftKlavTotalAttribute(cart) {
    if (syncingGiftKlavTotal || reconciling) {
      return Promise.resolve(cart);
    }

    const run = (currentCart) => {
      if (!currentCart) return null;
      const nextValue = formatGiftKlavTotal(computeGiftKlavTotalCents(currentCart));
      const attrs = currentCart.attributes || {};
      const prevValue = attrs[GIFT_KLAV_TOTAL_ATTR] != null ? String(attrs[GIFT_KLAV_TOTAL_ATTR]) : '';
      if (prevValue === nextValue) return currentCart;

      const nextAttributes = Object.assign({}, attrs, {
        [GIFT_KLAV_TOTAL_ATTR]: nextValue,
      });

      syncingGiftKlavTotal = true;
      return fetch(cartRoute('cartUpdate', '/cart/update.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: nextAttributes }),
      })
        .then((response) => {
          if (!response.ok) throw new Error(response.statusText || 'gift klav attribute update failed');
          return response.json();
        })
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.error('[gift-wrapping] _gift_klav_total sync failed', err);
          return currentCart;
        })
        .finally(() => {
          syncingGiftKlavTotal = false;
        });
    };

    if (cart) return Promise.resolve(run(cart));
    return fetchCart()
      .then(run)
      .catch(() => null);
  }

  function reconcileOrphans() {
    if (reconciling) return;
    const wrapIds = getWrapVariantIdSet();
    if (!wrapIds.size) {
      syncGiftKlavTotalAttribute();
      return;
    }

    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .then((cart) => {
        if (!cart || !Array.isArray(cart.items)) return;

        const presentKeys = new Set(cart.items.map((it) => String(it.key)));
        const updates = {};
        let hasUpdates = false;
        const transformMode = usesCartTransform();

        cart.items.forEach((it) => {
          if (!wrapIds.has(String(it.variant_id || it.id))) return;
          if (transformMode) {
            updates[it.key] = 0;
            hasUpdates = true;
            return;
          }
          const props = it.properties || {};
          const forKey = props[FOR_KEY_KEY];
          if (!forKey) return;
          if (!presentKeys.has(String(forKey)) || String(forKey) === String(it.key)) {
            updates[it.key] = 0;
            hasUpdates = true;
          }
        });

        if (!hasUpdates) {
          return syncGiftKlavTotalAttribute(cart);
        }

        armSwymSflSuppression();
        reconciling = true;
        return fetch(cartRoute('cartUpdate', '/cart/update.js'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates }),
        })
          .then(() => {
            reconciling = false;
            dispatchCartChange();
          })
          .catch(() => {
            reconciling = false;
          });
      })
      .catch(() => {});
  }

  document.addEventListener('on:cart:change', debounce(reconcileOrphans, 250));
  document.addEventListener('on:cart:change', debounce(() => {
    if (reconciling || syncingGiftKlavTotal) return;
    syncGiftKlavTotalAttribute();
  }, 400));

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => setTimeout(reconcileOrphans, 400));
  } else {
    setTimeout(reconcileOrphans, 400);
  }
})();
